import React from 'react';
import { Link } from 'react-router-dom';
import buildingImg from '../assets/building.png';
import SEO from '../components/SEO';

const Partner = () => {
  const opportunities = [
    { title: 'Land Owners', icon: 'map', desc: 'We offer joint development opportunities for landowners. Unlock the true value of your land with us.' },
    { title: 'Investors', icon: 'groups', desc: 'Partner with us to invest in high-potential real estate projects and earn promising returns.' },
    { title: 'Builders & Contractors', icon: 'engineering', desc: 'Collaborate with us on premium projects and deliver excellence together.' },
    { title: 'Channel Partners & Brokers', icon: 'real_estate_agent', desc: 'Grow your business with our premium projects and attractive partner benefits.' },
    { title: 'Professional Partners', icon: 'badge', desc: 'We collaborate with architects, legal experts, financial advisors, and other professionals.' },
  ];

  return (
    <div className="bg-[#041424] min-h-screen w-full font-sans text-white">
      <SEO 
        title="Partner With Us | Channel Partners | HBB0011 REALTY"
        description="Join the HBB0011 REALTY network as a channel partner or affiliate. Let's collaborate to redefine luxury real estate and architecture."
        keywords="real estate partners, channel partner network, affiliate real estate, collaborate with HBB0011 REALTY"
      />
      
      {/* 1. Hero Section (No Founder Image) */}
      <section className="relative w-full pt-32 pb-24 md:pt-40 md:pb-32 flex items-center overflow-hidden border-b border-gray-800">
        <div className="absolute inset-0 z-0">
          <img src={buildingImg} alt="Luxury Real Estate" className="w-full h-full object-cover opacity-20 mix-blend-luminosity" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#041424] via-[#041424]/90 to-transparent"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="flex items-center gap-2 text-xs sm:text-sm font-bold tracking-widest mb-6 uppercase">
            <Link to="/" className="text-white hover:text-[#D4AF37]">Home</Link>
            <span className="text-gray-500 material-symbols-outlined text-sm">chevron_right</span>
            <span className="text-[#D4AF37]">Partner With Us</span>
          </div>
          
          <h1 className="text-5xl sm:text-6xl md:text-7xl font-bold text-white mb-6 leading-tight tracking-tight uppercase">
            Partner <br/><span className="text-[#D4AF37]">With Us</span>
          </h1>
          <h2 className="text-xl md:text-2xl text-gray-300 font-light mb-8 uppercase tracking-widest leading-relaxed">
            Stronger Partnerships.<br/>Greater Possibilities.
          </h2>
          <p className="text-sm sm:text-base text-gray-400 mb-10 max-w-xl leading-relaxed">
            At HBB0011 Realty Realtors, we believe that great partnerships build exceptional projects. Let's join hands to create landmarks and deliver value together.
          </p>
        </div>
      </section>

      {/* 2. Why Partner Banner */}
      <section className="py-16 border-b border-gray-800 bg-[#06121e]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8">
          {[
            { icon: 'verified_user', title: 'Trusted Brand & Reputation' },
            { icon: 'handshake', title: 'Transparent Business Practices' },
            { icon: 'trending_up', title: 'High Growth Potential' },
            { icon: 'support_agent', title: 'Dedicated Support' },
            { icon: 'task_alt', title: 'Timely Quality Assurance' },
            { icon: 'grid_view', title: 'Diverse Portfolio' },
          ].map((item, i) => (
            <div key={i} className="flex flex-col items-center text-center group">
              <div className="w-14 h-14 rounded-full border border-[#D4AF37]/30 flex items-center justify-center mb-4 group-hover:bg-[#D4AF37]/10 transition-colors">
                <span className="material-symbols-outlined text-[#D4AF37] text-2xl font-light">{item.icon}</span>
              </div>
              <span className="text-[10px] text-white font-bold uppercase tracking-widest">{item.title}</span>
            </div>
          ))}
        </div>
      </section>

      {/* 3. Opportunities & Process Grid */}
      <section className="py-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12">
        
        {/* Left: Opportunities */}
        <div className="lg:col-span-7">
          <h3 className="text-lg font-bold text-white uppercase tracking-widest mb-10 border-b border-gray-800 pb-4">Partnership Opportunities</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {opportunities.map((opp, i) => (
              <div key={i} className="bg-[#06121e] border border-gray-800 p-6 rounded-sm group hover:border-[#D4AF37]/50 transition-colors">
                <span className="material-symbols-outlined text-[#D4AF37] text-3xl mb-4 font-light">{opp.icon}</span>
                <h4 className="text-white text-sm font-bold uppercase tracking-widest mb-3">{opp.title}</h4>
                <p className="text-gray-400 text-xs leading-relaxed mb-4">{opp.desc}</p>
                <Link to="#" className="text-[#D4AF37] text-[10px] font-bold uppercase tracking-widest flex items-center gap-1 hover:text-white transition-colors">
                  Learn More <span className="material-symbols-outlined text-sm">arrow_forward</span>
                </Link>
              </div>
            ))}
          </div>
        </div>

        {/* Right: Partnership Process */}
        <div className="lg:col-span-5 bg-[#06121e] border border-gray-800 p-8 sm:p-10 rounded-sm">
          <h3 className="text-lg font-bold text-white uppercase tracking-widest mb-10 border-b border-gray-800 pb-4">Our Partnership Process</h3>
          <div className="space-y-8">
            {[
              { num: '01', title: 'Connect With Us', desc: 'Share your interest and partnership goals.' },
              { num: '02', title: 'Discussion & Proposal', desc: 'We understand your expectations and present suitable opportunities.' },
              { num: '03', title: 'Agreement', desc: 'We finalize terms and establish a transparent agreement.' },
              { num: '04', title: 'Collaborate & Execute', desc: 'We work together with complete support.' },
              { num: '05', title: 'Grow Together', desc: 'Achieve success and build lasting relationships.' },
            ].map((step, i) => (
              <div key={i} className="flex gap-6 items-start">
                <span className="text-[#D4AF37] font-serif text-2xl font-bold mt-1">{step.num}</span>
                <div>
                  <h4 className="text-white text-sm font-bold uppercase tracking-wide mb-1">{step.title}</h4>
                  <p className="text-gray-400 text-xs">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 4. Let's Build Tomorrow Banner */}
      <section className="bg-[#06121e] border-y border-gray-800 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="text-3xl text-white font-light mb-4 uppercase">Let's Build a <span className="font-bold">Better Tomorrow</span></h2>
            <p className="text-gray-400 text-sm leading-relaxed mb-8">We are always open to new partnerships that create value for our customers, communities, and the future.</p>
            <div className="flex gap-6 text-center">
              {[
                { icon: 'favorite', label: 'Reliable Partnerships' },
                { icon: 'trending_up', label: 'Mutual Growth' },
                { icon: 'workspace_premium', label: 'Shared Success' }
              ].map((b, i) => (
                <div key={i} className="flex flex-col items-center">
                  <span className="material-symbols-outlined text-[#D4AF37] text-2xl mb-2">{b.icon}</span>
                  <span className="text-[9px] text-gray-400 uppercase tracking-widest">{b.label}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="flex justify-center lg:justify-end">
            <button className="bg-[#D4AF37] text-[#041424] font-bold text-xs uppercase tracking-widest py-4 px-8 rounded-sm hover:bg-white transition-colors">
              Become a Partner Today
            </button>
          </div>
        </div>
      </section>

      {/* 5. CTA Bar */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 bg-[#06121e] border border-gray-800 p-8 md:p-10 rounded-sm flex flex-col lg:flex-row items-center justify-between gap-8">
          <div className="flex items-center gap-6">
            <div className="w-16 h-16 rounded-full border border-[#D4AF37]/30 flex items-center justify-center shrink-0">
               <span className="material-symbols-outlined text-[#D4AF37] text-3xl font-light">handshake</span>
            </div>
            <div>
              <h3 className="text-xl sm:text-2xl text-white font-light mb-2">Ready to Partner With HBB0011?</h3>
              <p className="text-gray-400 text-xs">Join hands with a trusted real estate developer and create extraordinary success together.</p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 w-full lg:w-auto">
            <a href="tel:+919916710011" className="border border-white text-white px-6 py-4 rounded-sm font-bold text-[10px] uppercase tracking-widest hover:bg-white hover:text-[#041424] transition-colors flex items-center justify-center gap-2 w-full sm:w-auto">
              <span className="material-symbols-outlined text-base">call</span> +91 99167 10011
            </a>
            <a href="mailto:info@hbb0011realty.com" className="border border-white text-white px-6 py-4 rounded-sm font-bold text-[10px] uppercase tracking-widest hover:bg-white hover:text-[#041424] transition-colors flex items-center justify-center gap-2 w-full sm:w-auto">
              <span className="material-symbols-outlined text-base">mail</span> info@hbb0011realty.com
            </a>
          </div>
        </div>
      </section>

    </div>
  );
};

export default Partner;